import { useState } from "react";
import { BarChart3, Code2, Pause, Pencil, Play, Plus, RotateCcw, Search, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { EmbedCode } from "@/components/EmbedCode";
import { UsersDialog } from "@/components/super/UsersDialog";
import { CreateCustomerDialog } from "@/components/super/CreateCustomerDialog";
import { api, errMsg } from "@/lib/api";
import { fmtDate, fmtInt } from "@/lib/format";

const Act = ({ label, onClick, children, testId, danger }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button type="button" onClick={onClick} aria-label={label} data-testid={testId}
        className={`p-2 rounded-md transition-colors text-slate-500 ${danger ? "hover:text-red-600 hover:bg-red-50" : "hover:text-sky-700 hover:bg-sky-50"}`}>
        {children}
      </button>
    </TooltipTrigger>
    <TooltipContent>{label}</TooltipContent>
  </Tooltip>
);

const StatusPill = ({ c }) => {
  if (c.deleted) return <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700 font-medium">Slettet</span>;
  if (c.status === "paused") return <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-medium">Pauset</span>;
  return <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-medium">Aktiv</span>;
};

function Row({ c, onAction }) {
  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`customer-row-${c.id}`}>
      <td className="py-3 px-4"><div className="font-medium text-slate-900">{c.name}</div><div className="text-xs text-slate-500">Opprettet {fmtDate(c.created_at)}</div></td>
      <td className="py-3 px-4"><StatusPill c={c} /></td>
      <td className="py-3 px-4 font-num text-sm">{c.users_count}</td>
      <td className="py-3 px-4 font-num text-sm">{fmtInt(c.views_30d)}</td>
      <td className="py-3 px-4 font-num text-sm">{fmtInt(c.calcs_30d)}</td>
      <td className="py-3 px-4">
        {!c.deleted && (
          <Switch checked={c.pax_enabled !== false} onCheckedChange={(v) => onAction("pax", { ...c, pax_enabled: v })} aria-label="Skjema for passasjervederlag" data-testid={`customer-${c.id}-pax-switch`} />
        )}
      </td>
      <td className="py-3 px-4">
        {!c.deleted && (
          <Switch checked={!!c.anlop_enabled} onCheckedChange={(v) => onAction("anlop", { ...c, anlop_enabled: v })} aria-label="Anløpsliste" data-testid={`customer-${c.id}-anlop-switch`} />
        )}
      </td>
      <td className="py-2 px-2">
        <div className="flex justify-end">
          {c.deleted ? (
            <Act label="Gjenopprett" onClick={() => onAction("restore", c)} testId={`customer-${c.id}-restore`}><RotateCcw className="w-4 h-4" /></Act>
          ) : (
            <>
              <Act label="Rediger (emuler kundeadmin)" onClick={() => window.open(`/admin/${c.id}`, "_blank", "noopener")} testId={`customer-${c.id}-edit`}><Pencil className="w-4 h-4" /></Act>
              <Act label="Brukere og passord" onClick={() => onAction("users", c)} testId={`customer-${c.id}-users`}><Users className="w-4 h-4" /></Act>
              <Act label="Statistikk" onClick={() => onAction("stats", c)} testId={`customer-${c.id}-stats`}><BarChart3 className="w-4 h-4" /></Act>
              <Act label="Hent embed-kode" onClick={() => onAction("embed", c)} testId={`customer-${c.id}-embed`}><Code2 className="w-4 h-4" /></Act>
              <Act label={c.status === "paused" ? "Reaktiver" : "Sett på pause"} onClick={() => onAction("toggle", c)} testId={`customer-${c.id}-toggle-status`}>
                {c.status === "paused" ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
              </Act>
              <Act label="Slett" danger onClick={() => onAction("delete", c)} testId={`customer-${c.id}-delete`}><Trash2 className="w-4 h-4" /></Act>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

export const CustomersTable = ({ customers, showDeleted, setShowDeleted, reload, onStats }) => {
  const [q, setQ] = useState("");
  const [usersFor, setUsersFor] = useState(null);
  const [embedFor, setEmbedFor] = useState(null);
  const [deleteFor, setDeleteFor] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const run = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const onAction = (kind, c) => {
    if (kind === "users") setUsersFor(c);
    if (kind === "embed") setEmbedFor(c);
    if (kind === "delete") setDeleteFor(c);
    if (kind === "stats") onStats(c.id);
    if (kind === "restore") run(() => api.post(`/admin/customers/${c.id}/restore`), "Kunden er gjenopprettet");
    if (kind === "anlop") run(() => api.patch(`/admin/customers/${c.id}/anlop-module`, { enabled: c.anlop_enabled }), c.anlop_enabled ? "Anløpsliste er slått på for kunden" : "Anløpsliste er skjult for kunden");
    if (kind === "pax") run(() => api.patch(`/admin/customers/${c.id}/pax-module`, { enabled: c.pax_enabled }), c.pax_enabled ? "Skjema for passasjervederlag er slått på for kunden" : "Skjema for passasjervederlag er skjult for kunden");
    if (kind === "toggle") {
      const status = c.status === "paused" ? "active" : "paused";
      run(() => api.patch(`/admin/customers/${c.id}/status`, { status }), status === "paused" ? "Kunden er satt på pause" : "Kunden er reaktivert");
    }
  };

  const list = customers.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <TooltipProvider delayDuration={200}>
      <div className="bg-white border border-slate-200 rounded-lg" data-testid="customers-table">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between p-4 border-b border-slate-100">
          <div className="relative sm:w-72">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søk etter kunde" className="pl-9" data-testid="customer-search-input" />
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <Switch checked={showDeleted} onCheckedChange={setShowDeleted} data-testid="show-deleted-switch" />Vis slettede
            </label>
            <Button onClick={() => setCreateOpen(true)} data-testid="new-customer-button"><Plus className="w-4 h-4 mr-1" />Ny kunde</Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] text-left">
            <thead>
              <tr className="text-xs uppercase tracking-wide text-slate-500">
                <th className="py-2.5 px-4 font-medium">Kunde</th><th className="py-2.5 px-4 font-medium">Status</th>
                <th className="py-2.5 px-4 font-medium">Brukere</th><th className="py-2.5 px-4 font-medium">Visninger 30 d</th>
                <th className="py-2.5 px-4 font-medium">Beregninger 30 d</th><th className="py-2.5 px-4 font-medium">Passasjer&shy;vederlag</th><th className="py-2.5 px-4 font-medium">Anløpsliste</th><th className="py-2.5 px-4 font-medium text-right">Handlinger</th>
              </tr>
            </thead>
            <tbody>
              {list.map((c) => <Row key={c.id} c={c} onAction={onAction} />)}
              {!list.length && <tr><td colSpan={8} className="py-10 text-center text-sm text-slate-500">Ingen kunder funnet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <UsersDialog customer={usersFor} onOpenChange={(o) => !o && setUsersFor(null)} onChanged={reload} />
      <CreateCustomerDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={reload} />
      <Dialog open={!!embedFor} onOpenChange={(o) => !o && setEmbedFor(null)}>
        <DialogContent className="sm:max-w-2xl" data-testid="embed-dialog">
          <DialogHeader><DialogTitle>Embed-kode – {embedFor?.name}</DialogTitle></DialogHeader>
          {embedFor && <EmbedCode cid={embedFor.id} />}
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!deleteFor} onOpenChange={(o) => !o && setDeleteFor(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Slette «{deleteFor?.name}»?</AlertDialogTitle>
            <AlertDialogDescription>Kalkulatoren slutter å vises på kundens nettside og brukerne mister tilgang. Kunden kan gjenopprettes senere.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-customer"
              onClick={() => run(() => api.delete(`/admin/customers/${deleteFor.id}`), "Kunden er slettet")}>Slett</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TooltipProvider>
  );
};
